import { useQuery } from '@tanstack/react-query';

import { useNotification } from '@sine/sine-ui-components';

import mapToDropdownOptions from '../../helpers/mapToDropdownOptions';
import parseServerError from '../../helpers/parseServerError';
import { QueryKeys } from '../../query.client';
import zonesService from '../../services/zones/zones.service';
import { STORE_KEYS, store } from '../../store';
import type { OffsetLimit, SortDirection } from '../customHooks/useOffsetLimitPagination';

export function useZonesSearchQuery(
  siteId: string | undefined,
  searchTerm: string,
  offsetLimit: OffsetLimit,
  sortDirection: SortDirection,
) {
  const { addNotification } = useNotification();

  const {
    data: { data: zones = [], meta } = {},
    isLoading,
    isFetching,
    refetch: refetchZones,
  } = useQuery({
    queryKey: [QueryKeys.ZONES, siteId, searchTerm, offsetLimit, sortDirection],
    queryFn: async () => {
      if (!siteId) {
        return { data: [], meta: { total: 0, totalResults: 0, returnedResults: 0, page: 0, pages: 0 } };
      }

      return zonesService.searchZonesForSite(siteId, searchTerm, offsetLimit, sortDirection);
    },
    onError: (error: any) => {
      if (error.data?.code === 'FEATURE_DISABLED') {
        const zonesEnabled = store.getState()[STORE_KEYS.ZONES_ENABLED];
        // If zones is not enabled any User's teams, redirect user to activity view
        if (!zonesEnabled) window.location.href = ``;
      }

      addNotification({
        message: parseServerError(error),
        variant: 'danger',
      });
    },
    staleTime: 0,
    cacheTime: 0,
  });

  return {
    zones,
    hasNext: !!(meta && meta.page < (meta.pages || 0)),
    meta,
    isLoading,
    isFetching,
    refetchZones,
  };
}

export function useZonesQuery(siteId: string, offsetLimit?: OffsetLimit) {
  const { addNotification } = useNotification();
  const {
    data: { zones = [], zoneOptions = [] } = {},
    isLoading,
    isFetching,
    refetch: refetchZones,
  } = useQuery({
    enabled: !!siteId,
    queryKey: [QueryKeys.ZONES, siteId, offsetLimit],
    queryFn: async () => {
      try {
        const results = await zonesService.getZonesForSite(siteId, offsetLimit);

        return {
          zones: results,
          zoneOptions: mapToDropdownOptions(results),
        };
      } catch (error) {
        addNotification({
          message: parseServerError(error),
          variant: 'danger',
        });
        return {
          zones: [],
          zoneOptions: [],
        };
      }
    },
  });

  return {
    zones,
    zoneOptions,
    isLoading,
    isFetching,
    refetchZones,
  };
}

export function useZoneSiteHostGroupsQuery(siteId: string | undefined) {
  const {
    isLoading,
    isFetching,
    data: siteHostGroups = [],
  } = useQuery({
    enabled: !!siteId,
    queryKey: [QueryKeys.ZONE_HOSTGROUPS, siteId],
    queryFn: async () => zonesService.getAvailableHostGroups(siteId!),
    staleTime: 0,
    cacheTime: 0,
  });

  return {
    isLoading,
    isFetching,
    siteHostGroups,
  };
}

export function useZonesForHostProfileQuery(siteId: string, hostId: string) {
  const {
    isLoading: isLoadingZonesForHostProfile,
    isFetching: isFetchingZonesForHostProfile,
    data: zonesForHostProfile = [],
  } = useQuery({
    enabled: !!siteId && !!hostId,
    queryKey: [QueryKeys.ZONES_HOST_PROFILE, siteId, hostId],
    queryFn: async () => zonesService.getZonesForHostProfile(siteId, hostId),
  });

  return {
    isLoadingZonesForHostProfile,
    isFetchingZonesForHostProfile,
    zonesForHostProfile,
  };
}
